/* eslint-disable camelcase */
import pdfMake from 'addthaifont-pdfmake/build/pdfmake';
import 'addthaifont-pdfmake/build/vfs_fonts';
import dayjs from 'dayjs';

import buddhistEra from 'dayjs/plugin/buddhistEra';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import _ from 'lodash';
import groupByMaterialType from '../../utils/functions/groupByMaterialType';
import currencyFormatter from '../../utils/functions/currencyFormat';

dayjs.locale('th');
dayjs.extend(buddhistEra);
dayjs.extend(LocalizedFormat);

pdfMake.fonts = {
  Sarabun: {
    normal: 'Sarabun-Light.ttf',
    bold: 'Sarabun-Regular.ttf',
    italics: 'Sarabun-LightItalic.ttf',
    bolditalics: 'Sarabun-Italic.ttf',
  },
  Kanit: {
    normal: 'Kanit-Light.ttf',
    bold: 'Kanit-Regular.ttf',
    italics: 'Kanit-LightItalic.ttf',
    bolditalics: 'Kanit-Italic.ttf',
  },
  Prompt: {
    normal: 'Prompt-Light.ttf',
    bold: 'Prompt-Regular.ttf',
    italics: 'Prompt-LightItalic.ttf',
    bolditalics: 'Prompt-Italic.ttf',
  },
};

const calExpSumItemType = (item, mode = 'full') => {
  if (mode === 'full') {
    const sumItemType = _.reduce(
      item,
      (result, currentValue) => {
        if (!currentValue.fraction) result += currentValue.amount;
        return result;
      },
      0,
    );
    if (sumItemType === 0) {
      return '';
    }
    return `30x${sumItemType}`;
  }
  if (mode === 'fraction') {
    const sumItemType = _.map(item, (result) => {
      if (result.fraction) {
        return `ศ. ${result.buying_weight}x${result?.amount}`;
      }
      return '';
    });
    return _.reduce(
      sumItemType,
      (result, currentValue) => `${result} ${currentValue}`,
      '',
    );
  }

  return '';
};

const calSumItemType = (item, mode = 'full') => {
  if (mode === 'full') {
    const sumItemType = _.reduce(
      item,
      (result, currentValue) => {
        if (!currentValue.fraction) result += currentValue.amount;
        return result;
      },
      0,
    );
    if (sumItemType === 0) {
      return 0;
    }
    return sumItemType * 30;
  }
  if (mode === 'fraction') {
    const sumItemType = _.map(item, (result) => {
      if (result.fraction) {
        return result.buying_weight * result?.amount;
      }
      return 0;
    });
    return _.reduce(
      sumItemType,
      (result, currentValue) => {
        result += currentValue;
        return result;
      },
      0,
    );
  }

  return 0;
};

const genBody = (items) => {
  const newItems = _.map(items, (item, index) => [
    {
      text: `${index} ${calExpSumItemType(item, 'full')} ${calExpSumItemType(
        item,
        'fraction',
      )}`,
      colSpan: 2,
      alignment: 'center',
    },
    {},
    {
      text: currencyFormatter.format(
        calSumItemType(item, 'full') + calSumItemType(item, 'fraction'),
      ),
      alignment: 'center',
    },
    {
      text: ' - ',
      alignment: 'center',
    },
    {
      text: ' - ',
      alignment: 'center',
    },
  ]);
  return newItems;
};

const sumItemProp = (items, key) =>
  _.reduce(
    items,
    (result, currentValue) => {
      result += parseInt(currentValue[key], 10);
      return result;
    },
    0,
  );

// สำหรับหากระบะรวมจำนวนกิโลกรัมของท้ั้งใบ
const sumTotalItem = (items, mode) => {
  if (mode === 'full') {
    return _.reduce(
      items,
      (result, currentValue) => {
        if (!currentValue.fraction) result += currentValue.amount;
        return result;
      },
      0,
    );
  }
  if (mode === 'fraction') {
    return _.reduce(
      items,
      (result, currentValue) => {
        if (currentValue.fraction) result += currentValue.amount;
        return result;
      },
      0,
    );
  }
  if (mode === 'all') {
    return _.reduce(
      items,
      (result, currentValue) => {
        result += currentValue.amount;
        return result;
      },
      0,
    );
  }
  return 0;
};

const printPDF = (order, info) => {
  const { customer, createdAt } = order;
  const items = groupByMaterialType(order.items, 'type');
  const genItemBody = genBody(items);

  const docDefinition = {
    header: {
      columns: [
        {
          text: `${dayjs().format('DD/MM/BBBB')}`,
          alignment: 'right',
          margin: [10, 10, 10, 10],
          fontSize: '10',
        },
      ],
    },
    footer(currentPage, pageCount) {
      return [
        {
          text: `${currentPage.toString()} of ${pageCount}`,
          alignment: 'right',
          margin: [10, 10, 10, 10],
          fontSize: '10',
        },
      ];
    },

    pageSize: 'A5',
    pageOrientation: 'portait',
    pageMargins: [20, 20, 20, 20],
    defaultStyle: {
      font: 'Sarabun',
      fontSize: '10',
    },
    info: {
      title: `เลขที่เอกสาร : ${order?.vehicle_invoice.running_number}`,
    },
    content: [
      {
        text: `เลขที่ : ${order?.vehicle_invoice.running_number}`,
        alignment: 'right',
        fontSize: '10',
        bold: true,
      },
      {
        text: [
          {
            text: `${info?.name}\n`,
            fontSize: '10',
          },
          {
            text: `โทร. ${info?.owner.telephone}\t`,
          },
          {
            text: `แฟกซ์ ${info?.owner.fax}\n`,
          },
        ],
        alignment: 'center',
        fontSize: '10',
        bold: true,
      },
      '\n',
      {
        columns: [
          {
            text: [
              {
                text: `ลูกค้า (KEPADA) : ${customer.name}`,
                alignment: 'center',
                bold: true,
              },
            ],
          },
          {
            text: [
              {
                text: `วันที่ (TARIKH) : ${dayjs(createdAt).format(
                  'DD-MM-BBBB',
                )}\n`,
                alignment: 'center',
                bold: true,
              },
            ],
          },
        ],
      },
      '\n',
      {
        table: {
          widths: ['25%', '*', '*', '*', '*'],
          headerRows: 1,
          body: [
            [
              { text: 'รายการ\n(PERKARA)', colSpan: 2, alignment: 'center' },
              {},
              { text: 'จำนวน\n(KUANTITI)', alignment: 'center' },
              { text: 'ราคา\n(HARGA)', alignment: 'center' },
              { text: 'จำนวนเงิน\n(JUMLAH)', alignment: 'center' },
            ],
            ...genItemBody,
            [
              {
                text: 'รวม (JUMLAH)',
                alignment: 'center',
              },
              {
                text: `${currencyFormatter.format(
                  sumTotalItem(order.items, 'all'),
                )} กระบะ`,
                alignment: 'center',
              },
              {
                text: `${
                  currencyFormatter.format(
                    sumItemProp(order.items, 'selling_total_weight'),
                  ) || ' - '
                }`,
                alignment: 'center',
              },
              {
                text: `${' - '}`,
                alignment: 'center',
              },
              {
                text: `${
                  currencyFormatter.format(
                    sumItemProp(order.items, 'selling_total_price'),
                  ) || ' - '
                }`,
                alignment: 'center',
              },
            ],
          ],
        },
      },
      '\n\n',
      {
        columns: [
          {
            text: [
              {
                text: '..................................................................\n\n',
                fontSize: '8',
                alignment: 'center',
              },
              {
                text: 'ผู้ขาย (PENJUAL)',
                alignment: 'center',
              },
            ],
          },
          {
            text: [
              {
                text: '..................................................................\n\n',
                fontSize: '8',
                alignment: 'center',
              },
              {
                text: 'ผู้ซื้อ (DI TERIMA OLEH)',
                alignment: 'center',
              },
            ],
          },
        ],
      },
      '\n\n',
      {
        text: order.remark ? `หมายเหตุ : ${order.remark}` : '',
        fontSize: 10,
      },
    ],
    images: {
      checked:
        'data:image/png;base64,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',
      unchecked:
        'data:image/png;base64,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',
    },
  };
  pdfMake.createPdf(docDefinition).open();
};

export default printPDF;
